import type { RestaurantBranding, RestaurantBrandingWithFlag } from "api-types";
import { DocumentReference, getDoc, setDoc } from "firebase/firestore/lite";

import { FirestoreRefs } from "igfd-commons/src/firebase/refs";

export interface BrandingApi {
  /**
   * Returns the branding config for the given restaurant uuid if it exists, and is enabled for this
   * restaurant. Otherwise, returns an empty object.
   * @param restaurantUuid
   */
  fetchBranding(restaurantUuid: string): Promise<RestaurantBranding>;

  // Implicitly assumes that branding is enabled, or enables it if it is not.
  setBranding(
    restaurantUuid: string,
    branding: RestaurantBranding
  ): Promise<void>;
}

// This is the type of the document that is stored in Firestore.
type RestaurantBrandingFirestoreDocument = RestaurantBrandingWithFlag & {
  restaurantUuid: string;
  restaurantRef: DocumentReference;
};

/**
 * An implementation of the BrandingApi that uses Firestore as the backend.
 */
export class FirestoreBackedBrandingApi implements BrandingApi {
  private firestoreRefs: FirestoreRefs;

  constructor(firestoreRefs: FirestoreRefs) {
    this.firestoreRefs = firestoreRefs;
  }

  fetchBranding(restaurantUuid: string): Promise<RestaurantBranding> {
    return getDoc(this.firestoreRefs.branding(restaurantUuid)).then((doc) => {
      if (doc.exists()) {
        const data = doc.data() as RestaurantBrandingFirestoreDocument;
        // drop the fields that are not part of the RestaurantBranding type
        const { enabled, restaurantUuid, restaurantRef, ...branding } = data;
        if (!enabled) {
          // We don't want to return branding if it is not enabled.
          // Since this is used in different parts of the app.
          return {} as RestaurantBranding;
        }
        return branding as RestaurantBranding;
      } else {
        return {} as RestaurantBranding;
      }
    });
  }

  setBranding(
    restaurantUuid: string,
    branding: RestaurantBranding
  ): Promise<void> {
    const firestoreBranding = {
      ...branding,
      enabled: true, // If we have reached this point, we must have branding enabled.
      restaurantUuid,
      restaurantRef: this.firestoreRefs.restaurant(restaurantUuid),
    } as RestaurantBrandingFirestoreDocument;

    // Note: we aren't doing a merge here because we want to overwrite the
    // entire branding object, to ensure deleted fields are removed.
    return setDoc(
      this.firestoreRefs.branding(restaurantUuid),
      firestoreBranding
    );
  }
}
